import type { JSX } from 'react'
import type { INavGroup } from 'blocks/NavGroup'
import type { NavBarFormation } from './NavBar.h'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { NavDirection } from 'atoms/NavTab'
import { TextBoxColors } from 'atoms/TextBox'
import { MobileMenu } from './MobileMenu'
import {
  NavBarAlignItems,
  NavBarBackground,
  NavBarSearchPosition,
} from './NavBar.h'
import { NavBarSC } from './NavBar.styled'

export interface INavBar extends Omit<
  INavGroup,
  'children' | 'background' | 'alignItems'
> {
  children: React.ReactNode
  background?: NavBarBackground
  direction?: NavDirection
  alignItems?: NavBarAlignItems
  color?: TextBoxColors
  colorSelected?: TextBoxColors
  formation?: NavBarFormation
  searchPosition?: NavBarSearchPosition
  resetCheckedKey?: string
  isEmpty?: boolean
}

export const NavBar = ({
  children,
  background = NavBarBackground.BACKGROUND_DARK,
  direction = NavDirection.ROW,
  alignItems = NavBarAlignItems.Center,
  color = TextBoxColors.SURFACE_DARK_7,
  colorSelected = TextBoxColors.TEXT_ICON_CONSTANT_LIGHT,
  formation,
  width = 224,
  searchPosition = NavBarSearchPosition.Right,
  resetCheckedKey,
  isEmpty = false,
}: INavBar): JSX.Element => {
  const [isOpened, setIsOpened] = useState(false)
  const [widthClient, setWidthClient] = useState(0)
  const [_orientation, setOrientation] = useState(0)
  const isMobile = widthClient < 1240
  // (orientation === 0 && widthClient < 1241) || // for improvement mobile menu
  // (orientation === 1 && widthClient < 1241)
  const navBarRef = useRef<HTMLDivElement>(null)
  const onClick = useCallback(() => setIsOpened((isOpened) => !isOpened), [])

  useEffect(() => {
    if (navBarRef.current) {
      setOrientation(window.innerWidth > window.innerHeight ? 1 : 0)
      setWidthClient(window.innerWidth)
    }

    const updateWidth = (): void => {
      if (navBarRef.current) {
        setOrientation(window.innerWidth > window.innerHeight ? 1 : 0)
        setWidthClient(window.innerWidth)
      }
    }

    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  return (
    <NavBarSC
      colorDefault={color}
      direction={direction}
      alignItems={alignItems}
      background={background}
      isOpened={isOpened}
      formation={formation}
      width={width}
      searchPosition={searchPosition}
      ref={navBarRef}
      colorSelected={colorSelected}
    >
      {widthClient > 0 &&
        (isMobile ? (
          <MobileMenu
            background={background}
            color={color}
            resetCheckedKey={resetCheckedKey}
            isEmpty={isEmpty}
          >
            {children}
          </MobileMenu>
        ) : (
          React.Children.map(children, (child, index) =>
            React.isValidElement(child)
              ? React.cloneElement(child as React.ReactElement<any>, {
                  key: index,
                  isOpened,
                  onClick,
                  color,
                })
              : null
          )
        ))}
    </NavBarSC>
  )
}
